import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { withStyles } from '@mui/styles';
import impstyles from '../styles/Dashboard.module.scss';
import SideNav from './sidenav';
import RightGrid from './rightgrid';
import Snackbar from '@mui/material/Snackbar';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Context from '../context/Context';
import UserContext from '../context/UserContext';
import {
  getFeatureFlags,
  getMetaData,
  searchItems,
  searchItemsForVendors,
  searchWpidItemsInfo
} from '../services/apiService';
import { NONVARIANT_ENABLED } from '../constants';
import { getCurrentFirstDate } from '../services/calendarService';
import Header from '../layouts/header';
import Drawer from '@mui/material/Drawer';
import { ReactComponent as Union } from '../assets/union.svg';
import Bookmark from './bookmark';
import SearchResult from '../models/SearchResult';

const styles = theme => ({
  root: {
    flexGrow: 1
  }
});
const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-start',
  position: 'absolute',
  width: '360px',
  height: '80px',
  top: '0px',
  background: '#F7F8F9'
}));

export class Dashboard extends Component {
  constructor(props) {
    super(props);

    this.searchCallback = (
      showItemSearch,
      searchedItemNbrList,
      searchedVendorNbrList,
      searchedDCNbr,
      searchResult,
      searchedDcNbrList,
      // Explicit flag so WPID mode is a deterministic fact set at the moment
      // of search, rather than re-derived by checking whether the first
      // result happens to have a wpidNbr — which silently breaks on an
      // empty result set or a result missing the field. Defaults to false
      // so every existing (non-WPID) call site is unaffected.
      isWpidSearch = false
    ) => {
      this.setState({
        showItemSearch,
        searchedItemNbrList,
        searchedVendorNbrList,
        searchedDCNbr,
        searchResult,
        searchedDcNbrList,
        isWpidSearch
      });
    };

    const emptySearchResult = new SearchResult({
      itemNbr: 0,
      itemDesc: 'Not Available',
      item2Desc: 'Not Available',
      legacyItemNbr: 0,
      nineDigitVendorNbr: 'Not Available',
      upcNbr: 0,
      vendorName: 'Not Available',
      vendorNbr: 0,
      category: 0
    });

    this.itemSearchDcNavCallback = (newDC, currentItemIndex) => {
      searchItems(this.state.searchedItemNbrList, newDC)
        .then(searchResults => {
          if (searchResults.length > 0) {
            this.state.searchCallback(
              this.state.showItemSearch,
              this.state.searchedItemNbrList,
              this.state.searchedVendorNbrList,
              newDC,
              searchResults,
              this.state.searchedDcNbrList,
              false
            );
          } else {
            this.state.searchCallback(
              this.state.showItemSearch,
              this.state.searchedItemNbrList,
              this.state.searchedVendorNbrList,
              newDC,
              [emptySearchResult],
              this.state.searchedDcNbrList,
              false
            );
          }
          this.state.selectItemCallback(
            this.state.searchResult[currentItemIndex]
          );
          this.state.selectSavedSearchCallback(null);
        })
        .catch(err => {
          console.error(err);
          this.state.showMsgCallback('Something Went Wrong!');
        });
    };

    this.vendorSearchDcNavCallback = (newDC, currentItemIndex) => {
      searchItemsForVendors(this.state.searchedVendorNbrList, newDC)
        .then(searchResults => {
          let { itemInfo } = searchResults;
          if (itemInfo.length > 0) {
            this.state.searchCallback(
              this.state.showItemSearch,
              this.state.searchedItemNbrList,
              this.state.searchedVendorNbrList,
              newDC,
              itemInfo,
              this.state.searchedDcNbrList,
              false
            );
          } else {
            this.state.searchCallback(
              this.state.showItemSearch,
              this.state.searchedItemNbrList,
              this.state.searchedVendorNbrList,
              newDC,
              [emptySearchResult],
              this.state.searchedDcNbrList,
              false
            );
          }
          this.state.selectItemCallback(
            this.state.searchResult[currentItemIndex]
          );
          this.state.selectSavedSearchCallback(null);
        })
        .catch(err => {
          console.error(err);
          this.state.showMsgCallback('Something Went Wrong!');
        });
    };

    this.wpidSearchDcNavCallback = (newDC, currentItemIndex) => {
      const wpids = [...new Set(this.state.searchResult.map(r => r.wpidNbr))];
      searchWpidItemsInfo(wpids, newDC)
        .then(searchResults => {
          this.state.searchCallback(
            true,
            [],
            [],
            newDC,
            searchResults,
            this.state.searchedDcNbrList,
            true
          );
          this.state.selectItemCallback(
            searchResults[currentItemIndex] ?? searchResults[0]
          );
          this.state.selectSavedSearchCallback(null);
        })
        .catch(err => {
          console.error(err);
          this.state.showMsgCallback('Something Went Wrong!');
        });
    };

    this.getSelectedItemIndexCallback = () => {
      return this.state.searchResult.findIndex(
        item => item.itemNbr === this.state.selectedItem?.itemNbr
      );
    };

    this.selectItemCallback = searchResult => {
      this.setState({ selectedItem: searchResult });
    };

    this.firstDateChangeCallback = date => {
      this.setState({ firstDate: date });
    };

    this.setShowDurationSelector = show => {
      this.setState({ showDurationSelector: show });
    };

    this.showMsgCallback = msg => {
      this.setState({ message: msg });
    };

    this.selectSavedSearchCallback = savedSearch => {
      this.setState({
        selectedSavedSearch: savedSearch
      });
    };

    this.setNeedToFetchSavedSearch = () => {
      this.setState({
        needToFetchSavedSearch: true
      });
    };

    this.fetchSavedSearchCallBack = savedSearchList => {
      this.setState({
        savedSearchList: savedSearchList,
        needToFetchSavedSearch: false
      });
    };

    this.setColorThemeCallback = theme => {
      this.setState({ colorTheme: theme }, () => {
        if (typeof this.props.onThemeChange === 'function') {
          this.props.onThemeChange(theme);
        }
      });
    };

    this.setHasUnsavedChanges = hasChanges => {
      this.setState({ hasUnsavedChanges: hasChanges });
    };

    this.state = {
      // metadata
      dcNbrList: [],
      calStartDate: null,
      calEndDate: null,
      // feature flags — seeded with the build-time default as a fallback
      // until getFeatureFlags() resolves (or if it errors); the Akeyless-backed
      // live value from DDIR-BFF then takes over.
      nonVariantEnabled: NONVARIANT_ENABLED,
      // search
      showItemSearch: true,
      searchedItemNbrList: [],
      searchedVendorNbrList: [],
      searchedDcNbrList: [],
      searchedDCNbr: null,
      searchResult: null,
      isWpidSearch: false,
      searchCallback: this.searchCallback,
      itemSearchDcNavCallback: this.itemSearchDcNavCallback,
      vendorSearchDcNavCallback: this.vendorSearchDcNavCallback,
      wpidSearchDcNavCallback: this.wpidSearchDcNavCallback,
      // savedSearch
      selectedSavedSearch: null,
      showHideSidenav: this.showHideSidenav,
      closeSidenav: this.closeSidenav,
      savedSearchList: [],
      needToFetchSavedSearch: false,
      fetchSavedSearchCallBack: this.fetchSavedSearchCallBack,
      setNeedToFetchSavedSearch: this.setNeedToFetchSavedSearch,
      selectSavedSearchCallback: this.selectSavedSearchCallback,
      // selected item
      selectedItem: null,
      selectItemCallback: this.selectItemCallback,
      getSelectedItemIndexCallback: this.getSelectedItemIndexCallback,
      // calendar
      firstDate: getCurrentFirstDate(), // must be a valid Saturday
      firstDateChangeCallback: this.firstDateChangeCallback,
      // tab
      showDurationSelector: true,
      setShowDurationSelector: this.setShowDurationSelector,
      // snackbar
      message: null,
      showMsgCallback: this.showMsgCallback,
      showDrawer: true,
      showBookmarkDrawer: false,
      // week/compareweek view
      switchSearch: 'week',
      setSwitchSearch: this.setSwitchSearch,
      itemAvailable: true,
      setItemAvailable: this.setItemAvailable,
      // pending changes
      hasUnsavedChanges: false,
      setHasUnsavedChanges: this.setHasUnsavedChanges,
      // theme
      colorTheme: this.props.colorTheme || 'light',
      setColorTheme: this.setColorThemeCallback
    };

    this.showHideSidenav = this.showHideSidenav.bind(this);
    this.setSwitchSearch = this.setSwitchSearch.bind(this);
    this.resetElement = React.createRef();
    this.setItemAvailable = this.setItemAvailable.bind(this);
  }

  componentDidMount() {
    // Fetched independently of metaData — a slow or failed feature-flags call
    // should never block or break the rest of the dashboard load. On failure,
    // this.state.nonVariantEnabled simply keeps its build-time fallback value.
    getFeatureFlags()
      .then(flags => {
        this.setState({ nonVariantEnabled: flags.nonVariantEnabled });
      })
      .catch(err => {
        console.error('Failed to fetch feature flags, using build-time default:', err);
      });

    getMetaData()
      .then(metadata => {
        this.setState({
          dcNbrList: metadata.dcNbrList,
          calStartDate: metadata.startDate,
          calEndDate: metadata.endData
        });
        const params = new URLSearchParams(window.location.search);
        const itemNbr = params.get('item');
        const dcNbr = params.get('dc');
        if (itemNbr && dcNbr) {
          const parsedItem = parseInt(itemNbr, 10);
          const parsedDc = parseInt(dcNbr, 10);
          if (isNaN(parsedItem) || isNaN(parsedDc)) {
            this.showMsgCallback(`Invalid item/DC in URL: ${itemNbr}/${dcNbr}`);
            return;
          }
          searchItems([parsedItem], parsedDc)
            .then(results => {
              if (results.length > 0) {
                this.searchCallback(true, [parsedItem], [], parsedDc, results, [parsedDc]);
                this.selectItemCallback(results[0]);
              } else {
                this.showMsgCallback(`Item ${itemNbr} not found in DC ${dcNbr}`);
              }
            })
            .catch(err => {
              console.error(err);
              this.showMsgCallback('Something went wrong loading item from URL.');
            });
        }
      })
      .catch(err => {
        console.error(err);
        this.showMsgCallback(
          'Something went wrong, please refresh the page.'
        );
      });
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.colorTheme !== this.props.colorTheme &&
      this.props.colorTheme !== this.state.colorTheme
    ) {
      this.setState({ colorTheme: this.props.colorTheme });
    }
  }

  setSwitchSearch = value => {
    this.setState(prevState => ({
      ...prevState,
      switchSearch: value
    }));
  };

  setItemAvailable = value => {
    this.setState(prevState => ({
      ...prevState,
      itemAvailable: value
    }));
  };

  showHideSidenav = () => {
    if (this.state.showBookmarkDrawer) {
      this.showHideBookmarkDrawer();
    }
    this.setState(prevState => ({
      ...prevState,
      showDrawer: !prevState.showDrawer
    }));
  };

  closeSidenav = () => {
    if (this.state.showDrawer) {
      this.setState({
        showDrawer: false
      });
    }
  };

  showHideBookmarkDrawer = () => {
    if (this.state.showDrawer) {
      this.showHideSidenav();
    }
    this.setState(prevState => ({
      ...prevState,
      showBookmarkDrawer: !prevState.showBookmarkDrawer
    }));
  };

  handleMsgClose() {
    this.setState({ message: null });
  }

  handleReset = () => {
    this.setState({
      showItemSearch: true,
      searchedItemNbrList: [],
      searchedVendorNbrList: [],
      searchedDcNbrList: [],
      searchResult: null,
      selectedItem: null,
      isWpidSearch: false
    });
    this.resetElement.current.resetSearch();
  };

  render() {
    const { classes } = this.props;
    return (
      <UserContext.Consumer>
        {context => {
          const ctxValue = { ...this.state, user: context.user }; // NOSONAR - class component; useMemo is not available. Value recomputes only when Dashboard state or UserContext user changes.
          return (
          <Context.Provider value={ctxValue}>
            <div className={classes.root}>
              <div>
                <div className={`${impstyles.RightGrid} ${this.state.colorTheme === 'dark' ? impstyles.RightGridDark : ''}`}>
                  <Header
                    user={context.user}
                    authButtonMethod={context.logout}
                    showHideSidenav={this.showHideSidenav}
                    showHideBookmarkDrawer={this.showHideBookmarkDrawer}
                  />
                  <RightGrid />
                </div>
                <div>
                  <Drawer
                    sx={{
                      maxWidth: 387,
                      flexShrink: 1,
                      '& .MuiDrawer-paper': {
                        width: 387,
                        height: '100vh',
                        top: 0,
                        background: this.state.colorTheme === 'dark' ? '#121212' : '#FFFFFF',
                        overflow: 'hidden'
                      }
                    }}
                    ModalProps={{ keepMounted: true, onBackdropClick: this.showHideSidenav }}
                    anchor="right"
                    open={this.state.showDrawer}
                  >
                    <div className={`${impstyles.drawerHeader} ${this.state.colorTheme === 'dark' ? impstyles.drawerHeaderDark : ''}`}>
                      <div className={impstyles.unionIcon}>
                        <Union />
                      </div>
                      <div className={impstyles.headerTextContainer}>
                        <h3 className={impstyles.headerTextCss}>
                          What are you looking for?
                        </h3>
                        <h6 className={impstyles.headerSubtextCss}>
                          Search for specific items
                        </h6>
                      </div>
                      <div>
                        <button
                          type="button"
                          data-testid="resetButton"
                          id="resetButton"
                          className={impstyles.resetButton}
                          onClick={this.handleReset}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            font: 'inherit',
                            cursor: 'pointer'
                          }}
                        >
                          Reset
                        </button>
                        <div className={impstyles.spacer} />
                      </div>
                    </div>
                    <SideNav resetReference={this.resetElement} />
                  </Drawer>
                  <Bookmark
                    showBookmarkDrawer={this.state.showBookmarkDrawer}
                    showHideBookmarkDrawer={this.showHideBookmarkDrawer}
                  />
                </div>
              </div>
              <Snackbar
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left'
                }}
                open={this.state.message}
                autoHideDuration={6000}
                onClose={this.handleMsgClose.bind(this)}
                message={this.state.message}
                action={
                  <IconButton
                    size="small"
                    aria-label="close"
                    color="inherit"
                    onClick={this.handleMsgClose.bind(this)}
                    data-testid="iconButton"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                }
              />
            </div>
          </Context.Provider>
          );
        }}
      </UserContext.Consumer>
    );
  }
}

Dashboard.contextType = UserContext;

Dashboard.propTypes = {
  classes: PropTypes.object.isRequired,
  colorTheme: PropTypes.string,
  onThemeChange: PropTypes.func
};

export default withStyles(styles)(Dashboard);